/**
 * HITL request helpers — promise wrappers around the existing `copilot:preview`
 * event + <HITLGate> confirmation UI.
 *
 * These let non-chat surfaces (SuggestLinks, RegImpactPanel, GenerateRecord …)
 * route a proposed write / navigation through the SAME human-in-the-loop gate
 * the GrcCopilotSidebar uses, and await the user's decision.
 */
import { guardBackendTool, executeBackendTool, type BackendToolCall, type ToolResult } from './tool-executor'
import { executeAction, type UiAction } from './action-bridge'
import type { CopilotPreviewPayload } from './HITLGate'
import type { ActionGuardResult } from './action-guard'

/**
 * Preview a backend write (POST/PUT/…) in the HITL gate; the network call runs
 * only after the user confirms. Resolves with the ToolResult on confirm, or
 * `null` when the user cancels.
 */
export function hitlBackendTool(
  tool: BackendToolCall,
  describe?: { system?: string },
): Promise<ToolResult | null> {
  return new Promise((resolve) => {
    const guard = guardBackendTool(tool)
    const payload: CopilotPreviewPayload = {
      requestId: `hitl:${tool.capability_code}:${Date.now()}`,
      actions: [
        {
          op: 'submitToExternalSystem',
          tool_name: tool.capability_code,
          side_effect: guard.actionClass,
          system: describe?.system ?? `${(tool.method ?? '').toUpperCase()} ${tool.path}`,
        } as unknown as UiAction,
      ],
      guardResult: guard,
      currentValues: {},
      proposedValues: (tool.body as Record<string, unknown>) ?? {},
      onConfirm: async (edited) => {
        const finalTool: BackendToolCall =
          edited && Object.keys(edited).length > 0
            ? { ...tool, body: { ...(tool.body ?? {}), ...edited } }
            : tool
        const result = await executeBackendTool(finalTool)
        resolve(result)
      },
      onCancel: () => resolve(null),
    }
    window.dispatchEvent(new CustomEvent('copilot:preview', { detail: payload }))
  })
}

/**
 * Preview a navigation in the HITL gate (preview → confirm → navigate). Resolves
 * `true` on confirm+navigate, `false` on cancel.
 */
export function hitlNavigate(to: string): Promise<boolean> {
  return new Promise((resolve) => {
    const guardResult: ActionGuardResult = { allowed: true, requiresConfirm: true, actionClass: 'read' }
    const payload: CopilotPreviewPayload = {
      requestId: `hitl:nav:${to}:${Date.now()}`,
      actions: [{ op: 'navigate', to }],
      guardResult,
      currentValues: {},
      proposedValues: {},
      onConfirm: async () => {
        await executeAction({ op: 'navigate', to })
        resolve(true)
      },
      onCancel: () => resolve(false),
    }
    window.dispatchEvent(new CustomEvent('copilot:preview', { detail: payload }))
  })
}

/**
 * Preview proposed form values (a fillForm-style diff) in the HITL gate.
 * Resolves with the (possibly user-edited) values on confirm, or `null` on cancel.
 */
export function hitlProposeValues(
  formId: string,
  values: Record<string, unknown>,
): Promise<Record<string, unknown> | null> {
  return new Promise((resolve) => {
    const guardResult: ActionGuardResult = {
      allowed: true,
      requiresConfirm: true,
      actionClass: 'write',
    }
    const payload: CopilotPreviewPayload = {
      requestId: `hitl:fill:${formId}:${Date.now()}`,
      actions: [{ op: 'fillForm', formId, values, preview: true }],
      guardResult,
      currentValues: {},
      proposedValues: values,
      onConfirm: (edited) => {
        const final = edited && Object.keys(edited).length > 0 ? edited : values
        resolve(final)
      },
      onCancel: () => resolve(null),
    }
    window.dispatchEvent(new CustomEvent('copilot:preview', { detail: payload }))
  })
}
